import { desanitizeKey } from '@/helper/utils';
import { RootState } from '@/store';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';

interface ReviewAndCreateProps {
  form: {
    blueNumberId: string;
    phoneNumber: string;
    roleId: number;
    welcomeMsg: string;
  };
  selectedPermissions: Record<string, string[]>;
  accessLevel: 'full' | 'restricted' | 'readonly';
  settings: {
    mfa: boolean;
    autoTimeout: boolean;
    ipRestriction: boolean;
    auditLogging: boolean;
    timeRestriction: boolean;
  };
  maxUsers: number | '';
  sessionDuration: string;
  isConfirmed: boolean;
  setIsConfirmed: (isConfirmed: boolean) => void;
  selectedRole: number;
  welcomeMessage: string;
  isEditedMode: boolean;
}

const ReviewAndCreate: React.FC<ReviewAndCreateProps> = ({
  form,
  selectedPermissions,
  accessLevel,
  settings,
  maxUsers,
  sessionDuration,
  isConfirmed,
  setIsConfirmed,
  selectedRole,
  welcomeMessage,
  isEditedMode,
}) => {
  const [error, setError] = useState<string>('');

  // Get role name from Redux store

  const roles = useSelector((state: RootState) => state.rolesPermissions?.filteredRoles || []);
  const selectedRoleData = roles.find((role: any) => role.id === selectedRole);
  const roleName = selectedRoleData?.name || 'N/A';
  const roleDescription = selectedRoleData?.description || 'N/A';

  return (
    <>
      <div className="rounded-2xl mx-auto text-[#621C43]">
        <div className="border-b border-b-[#621C43] px-6 py-5">
          <h2 className="font-Lato text-[28px] font-bold">
            {isEditedMode ? 'Review & Update' : 'Review & Create'}
          </h2>
          <p className="font-Lato text-[20px] font-Medium">
            {`Review all information before ${
              isEditedMode ? 'updating' : 'creating'
            } the admin user`}
          </p>
        </div>

        <div className="p-6">
          {/* Personal Info and Role Section */}
          <div className="grid md:grid-cols-2 gap-6 mb-6">
            {/* Personal Information */}
            <div className="flex flex-col border border-[#87074C40] bg-[#FFF8ED4D] rounded-lg p-4">
              <h4 className="font-Lato font-semibold text-[16px] text-[#4A082D] mb-3">
                Personal Information
              </h4>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="font-light text-[14px] text-[#621C43]">Mobile Number:</span>
                  <span className="font-semibold text-[14px] text-[#4A082D]">
                    {form.phoneNumber || 'N/A'}
                  </span>
                </div>
              </div>
            </div>

            {/* Role & Permissions Section */}
            <div className="flex flex-col border border-[#87074C40] bg-[#FFF8ED4D] rounded-lg p-4">
              <h4 className="font-Lato font-semibold text-[16px] text-[#4A082D] mb-3">
                Role Assignment
              </h4>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="font-light text-[14px] text-[#621C43]">Assigned Role:</span>
                  <span className="font-semibold text-[14px] text-[#4A082D]">
                    {desanitizeKey(roleName)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="font-light text-[14px] text-[#621C43]">Permissions:</span>
                  <span className="font-semibold text-[14px] text-[#4A082D] text-right max-w-[200px] line-clamp-4">
                    {roleDescription}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Error Display */}
          {error && (
            <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
              {error}
            </div>
          )}

          {/* Confirm Checkbox */}
          <div className="mt-6 bg-[#4A082D33] border rounded-lg p-4">
            <label className="flex items-start font-Lato font-medium text-[14px] text-[#4A082D] gap-2 cursor-pointer">
              <input
                type="checkbox"
                className="mt-1 accent-[#621C43] cursor-pointer"
                checked={isConfirmed}
                onChange={(e) => setIsConfirmed(e.target.checked)}
              />
              <span>
                I confirm that all the information provided is accurate and I want to
                {isEditedMode ? ' update' : ' create'} this admin user.
              </span>
            </label>
            <p className="text-xs font-Lato text-[#4A082D] mt-2 ml-6 italic">
              {isEditedMode
                ? 'This action will update admin user with the specified permissions  '
                : 'This action will create a new admin user with the specified permissions and send credentials as configured.'}
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default ReviewAndCreate;
